// Case Buddy addendum path-ending review helpers.
// Spreadsheet-shaped rows remain the source of truth; this module only builds review/output views.
import { CASE_COLUMN_HEADERS, CASE_DEFAULT_COLS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';
import { caseBuddyHashString, escapeRegExp, normalizeCell, normalizeComparisonKey, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { rowLooksLikeCaseBuddyHeader } from './CaseBuddySheetData.js';
import {
  CASE_TRACE_DIRECTION_FORWARD,
  CASE_TRACE_DIRECTION_REVERSE,
  buildCaseTraceDirectionContextByRowIndex,
  getCaseTraceDirectionRelevantAttribution,
  getCaseTraceEndpointAddress,
  normalizeCaseTraceDirection
} from './CaseBuddyTraceDirection.js';

export const CASE_ADDENDUM_PATH_ENDING_REVIEW_ID = 'addendum_path_endings';

export const CASE_ADDENDUM_PATH_ENDING_REVIEW_HEADERS = Object.freeze(CASE_COLUMN_HEADERS.slice(0, CASE_DEFAULT_COLS));

export const CASE_ADDENDUM_PATH_ENDING_OMIT_REASON_LABELS = Object.freeze({
  no_exchange_keyword: 'No exchange keyword in Column J',
  victim_owned_keyword: "Victim-owned exchange wording",
  not_path_ending: 'Not a path-ending row',
  duplicate: 'Duplicate filtered row',
  keyword_list_empty: 'Exchange keyword list empty'
});

export function normalizeCaseAddendumKeyword(value = '') {
  return normalizeCell(value)
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeCaseAddendumKeywordList(values = []) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((item) => normalizeCaseAddendumKeyword(item?.name || item?.presentedName || item?.canonicalName || item))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => right.length - left.length || left.localeCompare(right));
}

export function buildCaseAddendumExchangeKeywordList(effectiveCurator = {}) {
  return normalizeCaseAddendumKeywordList(effectiveCurator.exchange || effectiveCurator.exchanges || []);
}

export function caseAddendumNoteIncludesKeyword(note = '', keyword = '') {
  const haystack = normalizeCell(note).toLowerCase();
  const needle = normalizeCaseAddendumKeyword(keyword).toLowerCase();
  return !!needle && haystack.includes(needle);
}

export function findCaseAddendumKeywordMatches(note = '', keywords = []) {
  const normalizedKeywords = normalizeCaseAddendumKeywordList(keywords);
  return normalizedKeywords.filter(keyword => caseAddendumNoteIncludesKeyword(note, keyword));
}

export function caseAddendumNoteHasVictimOwnedKeyword(note = '', keywords = []) {
  const source = normalizeCell(note);
  if (!source) return false;
  return normalizeCaseAddendumKeywordList(keywords).some((keyword) => {
    const escaped = escapeRegExp(keyword);
    return new RegExp(`(?:victim['’]s|victims)\\s+${escaped}`, 'i').test(source);
  });
}

export function isCaseAddendumPathEndingRow(row = []) {
  return /\bPath\s+ends\./i.test([row?.[16], row?.[9]].map(normalizeCell).filter(Boolean).join(' '));
}

export function buildCaseAddendumDuplicateKey(row = []) {
  return [row?.[0], row?.[1], row?.[3], row?.[9], row?.[12], row?.[16]]
    .map(normalizeComparisonKey)
    .join('|');
}

export function buildCaseAddendumPathEndingReviewState(sourceRows = [], exchangeKeywords = [], workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(Array.isArray(sourceRows) ? sourceRows : [], 0, CASE_DEFAULT_COLS);
  const keywords = normalizeCaseAddendumKeywordList(exchangeKeywords);
  const requirePathEnds = options.requirePathEnds !== false;
  const dedupe = options.dedupe !== false;
  const prerequisiteCanBuild = options.prerequisiteCanBuild !== false;
  const prerequisiteReviewIsCurrent = !!options.prerequisiteReviewIsCurrent;
  const omittedReasonCounts = {};
  const traceDirectionContextByRowIndex = buildCaseTraceDirectionContextByRowIndex(rows);
  const reviewRows = [];
  const includedRows = [];
  const seenIncluded = new Set();
  const lastKnownValues = { pathLabel: '', hash: '', fromAddress: '' };
  let skippedBlankCount = 0;
  let skippedHeaderCount = 0;
  let sourceMeaningfulRowCount = 0;
  let pathEndingSourceRowCount = 0;

  const countReason = (reason) => {
    if (!reason) return;
    omittedReasonCounts[reason] = (omittedReasonCounts[reason] || 0) + 1;
  };

  rows.forEach((sourceRow, sourceRowIndex) => {
    const baseRow = normalizeCaseMatrix([sourceRow], 1, CASE_DEFAULT_COLS)[0];
    if (!rowHasMeaningfulData(baseRow)) {
      skippedBlankCount += 1;
      return;
    }
    if (rowLooksLikeCaseBuddyHeader(baseRow)) {
      skippedHeaderCount += 1;
      return;
    }

    sourceMeaningfulRowCount += 1;
    if (normalizeCell(baseRow[1])) {
      lastKnownValues.pathLabel = normalizeCell(baseRow[0]) || lastKnownValues.pathLabel;
      lastKnownValues.hash = normalizeCell(baseRow[1]) || lastKnownValues.hash;
      lastKnownValues.fromAddress = normalizeCell(baseRow[2]) || lastKnownValues.fromAddress;
    }

    const rowWithContext = baseRow.slice(0, CASE_DEFAULT_COLS);
    rowWithContext[0] = normalizeCell(rowWithContext[0]) || lastKnownValues.pathLabel;
    rowWithContext[1] = normalizeCell(rowWithContext[1]) || lastKnownValues.hash;
    rowWithContext[2] = normalizeCell(rowWithContext[2]) || lastKnownValues.fromAddress;

    const traceDirectionContext = traceDirectionContextByRowIndex.get(sourceRowIndex) || { traceDirection: CASE_TRACE_DIRECTION_FORWARD };
    const traceDirection = normalizeCaseTraceDirection(traceDirectionContext.traceDirection) || CASE_TRACE_DIRECTION_FORWARD;
    const isReverse = traceDirection === CASE_TRACE_DIRECTION_REVERSE;
    const note = normalizeCell(baseRow[9]);
    const attribution = getCaseTraceDirectionRelevantAttribution(rowWithContext, traceDirection);
    const reverseCaseNotes = isReverse ? normalizeCell(baseRow[22]) : '';
    const reviewText = isReverse ? [note, attribution, reverseCaseNotes].filter(Boolean).join(' | ') : note;
    const matchedKeywords = findCaseAddendumKeywordMatches(reviewText, keywords);
    const hasVictimOwnedKeyword = caseAddendumNoteHasVictimOwnedKeyword(reviewText, keywords);
    const isPathEnding = isCaseAddendumPathEndingRow(baseRow);
    if (isPathEnding) pathEndingSourceRowCount += 1;

    let status = 'included';
    let omitReason = '';
    if (keywords.length === 0) {
      status = 'omitted';
      omitReason = 'keyword_list_empty';
    } else if (matchedKeywords.length === 0) {
      status = 'omitted';
      omitReason = 'no_exchange_keyword';
    } else if (hasVictimOwnedKeyword) {
      status = 'omitted';
      omitReason = 'victim_owned_keyword';
    } else if (requirePathEnds && !isPathEnding) {
      status = 'omitted';
      omitReason = 'not_path_ending';
    } else if (dedupe) {
      const duplicateKey = buildCaseAddendumDuplicateKey(rowWithContext);
      if (seenIncluded.has(duplicateKey)) {
        status = 'omitted';
        omitReason = 'duplicate';
      } else {
        seenIncluded.add(duplicateKey);
      }
    }

    if (status === 'omitted') countReason(omitReason);

    const reviewRow = {
      key: `addendum-path-ending-${sourceRowIndex}-${caseBuddyHashString(rowWithContext.join('|'))}`,
      status,
      statusLabel: status === 'included' ? 'Included' : 'Omitted',
      omitReason,
      reasonLabel: status === 'included' ? 'Eligible for addendum review' : (CASE_ADDENDUM_PATH_ENDING_OMIT_REASON_LABELS[omitReason] || 'Omitted'),
      sourceRowIndex,
      sourceRowNumber: sourceRowIndex + 1,
      pathLabel: normalizeCell(rowWithContext[0]),
      transactionHash: normalizeCell(rowWithContext[1]),
      fromAddress: normalizeCell(rowWithContext[2]),
      toAddress: normalizeCell(rowWithContext[3]),
      ...(isReverse
        ? {
          endpointAddress: getCaseTraceEndpointAddress(rowWithContext, traceDirection),
          traceDirection: CASE_TRACE_DIRECTION_REVERSE
        }
        : {}),
      utcDate: normalizeCell(rowWithContext[4]),
      amount: normalizeCell(rowWithContext[5]),
      token: normalizeCell(rowWithContext[6]),
      notes: note,
      attribution,
      ...(isReverse
        ? {
          toAttribution: normalizeCell(rowWithContext[10]),
          fromAttribution: normalizeCell(rowWithContext[23])
        }
        : {}),
      analystNotes: normalizeCell(rowWithContext[11]),
      fundsFollowed: normalizeCell(rowWithContext[12]),
      fromHash: normalizeCell(rowWithContext[14]),
      continuesAs: normalizeCell(rowWithContext[16]),
      matchedKeywords,
      matchedKeywordsText: matchedKeywords.join(', '),
      hasVictimOwnedKeyword,
      isPathEnding,
      outputRow: rowWithContext,
      searchText: [
        sourceRowIndex + 1,
        status,
        CASE_ADDENDUM_PATH_ENDING_OMIT_REASON_LABELS[omitReason] || '',
        rowWithContext[0],
        rowWithContext[1],
        rowWithContext[2],
        rowWithContext[3],
        rowWithContext[4],
        rowWithContext[5],
        rowWithContext[6],
        rowWithContext[9],
        rowWithContext[10],
        rowWithContext[11],
        rowWithContext[12],
        rowWithContext[16],
        ...(isReverse ? [rowWithContext[22], rowWithContext[23], CASE_TRACE_DIRECTION_REVERSE] : []),
        matchedKeywords.join(' ')
      ].map(value => normalizeCell(value).toLowerCase()).join(' ')
    };

    reviewRows.push(reviewRow);
    if (status === 'included') includedRows.push(reviewRow);
  });

  const omittedCount = reviewRows.length - includedRows.length;
  const currentBasisHash = `addendum-path-endings-${caseBuddyHashString(JSON.stringify({
    keywordCount: keywords.length,
    keywords,
    requirePathEnds,
    dedupe,
    whatWentWhereBasisHash: normalizeCell(options.whatWentWhereBasisHash),
    rows: reviewRows.map(row => normalizeCaseTraceDirection(row.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
      ? [row.status, row.omitReason, row.sourceRowIndex, row.pathLabel, row.transactionHash, CASE_TRACE_DIRECTION_REVERSE, row.endpointAddress, row.notes, row.attribution, row.continuesAs, row.matchedKeywordsText]
      : [row.status, row.omitReason, row.sourceRowIndex, row.pathLabel, row.transactionHash, row.toAddress, row.notes, row.continuesAs, row.matchedKeywordsText])
  }))}`;
  const review = workflowState?.addendumPathEndingReview || null;
  const caseDataRevision = Number(options.caseDataRevision) || 0;
  const reviewIsCurrent = !!review?.reviewedAtUtc && review.basisHash === currentBasisHash && Number(review.revision) === caseDataRevision;
  const canReview = prerequisiteCanBuild && keywords.length > 0;

  return {
    reviewRows,
    includedRows,
    omittedRows: reviewRows.filter(row => row.status === 'omitted'),
    sourceMeaningfulRowCount,
    skippedBlankCount,
    skippedHeaderCount,
    pathEndingSourceRowCount,
    includedCount: includedRows.length,
    omittedCount,
    omittedReasonCounts,
    keywordCount: keywords.length,
    keywords,
    requirePathEnds,
    dedupe,
    prerequisiteCanBuild,
    prerequisiteReviewIsCurrent,
    prerequisiteSummary: normalizeCell(options.prerequisiteSummary),
    canReview,
    currentBasisHash,
    reviewIsCurrent,
    reviewedAtUtc: review?.reviewedAtUtc || '',
    reviewedRevision: Number(review?.revision),
    summary: !prerequisiteCanBuild
      ? (normalizeCell(options.prerequisiteSummary) || 'Build and review What Went Where before filtering exchange path-ending rows for the addendum.')
      : keywords.length === 0
        ? 'No exchange keywords are loaded yet. Refresh the keyword lists or add local exchange curator terms before reviewing addendum path endings.'
        : `${includedRows.length} exchange path-ending row${includedRows.length === 1 ? '' : 's'} included for addendum review. ${omittedCount} row${omittedCount === 1 ? '' : 's'} omitted due to exclusion criteria.`
  };
}

export function buildCaseAddendumPathEndingReviewOutputRows(state = {}) {
  return [
    CASE_ADDENDUM_PATH_ENDING_REVIEW_HEADERS.slice(),
    ...(Array.isArray(state.includedRows) ? state.includedRows : []).map(row => normalizeCaseMatrix([row.outputRow], 1, CASE_DEFAULT_COLS)[0].slice(0, CASE_DEFAULT_COLS))
  ];
}

export function buildCaseAddendumPathEndingReviewAuditRows(state = {}, actionLabel = 'Addendum path-ending review') {
  const reasonCounts = state.omittedReasonCounts || {};
  const reasonRows = Object.keys(CASE_ADDENDUM_PATH_ENDING_OMIT_REASON_LABELS).map(reason => [
    CASE_ADDENDUM_PATH_ENDING_OMIT_REASON_LABELS[reason],
    reasonCounts[reason] || 0
  ]);

  return [
    ['Case Buddy Addendum Path-Ending Review Audit', '', '', '', '', '', '', '', '', 'Audit only. Built from current What Went Where output and exchange keyword review; Main Data is not modified.'],
    ['Action', actionLabel, 'Basis', state.currentBasisHash || '', 'Reviewed Current', state.reviewIsCurrent ? 'Yes' : 'No'],
    ['Source Rows Evaluated', state.sourceMeaningfulRowCount || 0, 'Path-Ending Source Rows', state.pathEndingSourceRowCount || 0, 'Exchange Keywords', state.keywordCount || 0],
    ['Included Rows', state.includedCount || 0, 'Omitted Due To Exclusion Criteria', state.omittedCount || 0, 'Require Path Ends', state.requirePathEnds ? 'Yes' : 'No', 'Deduplicate', state.dedupe ? 'Yes' : 'No'],
    [],
    ['Omission Reason', 'Count'],
    ...reasonRows,
    [],
    ['Included Exchange Path-Ending Rows'],
    CASE_ADDENDUM_PATH_ENDING_REVIEW_HEADERS.slice(),
    ...(Array.isArray(state.includedRows) ? state.includedRows : []).map(row => normalizeCaseMatrix([row.outputRow], 1, CASE_DEFAULT_COLS)[0].slice(0, CASE_DEFAULT_COLS)),
    [],
    ['Omitted Rows'],
    ['Source Row', 'Reason', 'Path Label', 'Transaction Hash', 'To Address', 'Matched Keywords', 'Column J Notes', 'Column Q Continues As'],
    ...(Array.isArray(state.omittedRows) ? state.omittedRows : []).map(row => [row.sourceRowNumber, row.reasonLabel, row.pathLabel, row.transactionHash, row.toAddress, row.matchedKeywordsText, row.notes, row.continuesAs])
  ];
}
